/**
 * Cliente REST do mtr-manager — MTR e CDF das coletas.
 *
 * Chamada direta ao serviço com o token do próprio usuário, como no musa-agreements. O
 * mtr-manager ainda não escopa tokens do realm `generator`: o portal só pergunta pela coleta
 * que está mostrando, cuja visibilidade já foi decidida pela listagem de coletas.
 */

import { getValidToken } from '@/integrations/keycloak/client';

const API_URL = import.meta.env.VITE_MTR_MANAGER_URL;

/** `MtrSyncStatusEnum` do serviço. */
export type PdfSyncStatus = 'pending' | 'success' | 'error';

/** Item de `GET /v1/manifests` — só o que o portal consome. */
export interface Manifest {
  id: number;
  collect_id: string;
  /** `ManifestStatusEnum`: `issued`, `received`, `canceled`, `pending`… */
  status: string;
  number: string | null;
  /** PDF do MTR no S3; vazio enquanto o órgão não devolveu o arquivo. */
  file: string | null;
  issuer: string;
  /** Id do CDF vinculado, quando o órgão já emitiu. */
  certificate: number | null;
  updated_at: string;
  deleted: boolean;
}

/** Linha de `GET /v1/mtr-collects`: manifesto enriquecido, já com o número do CDF. */
export interface MtrCollect {
  id: number;
  collect_id: string;
  status: string;
  number: string | null;
  file: string | null;
  issuer: string;
  cdf_number: string | null;
  updated_at: string | null;
}

/** Resposta de `POST /v1/manifests/{id}/pdf/download`. `url` nula = download ainda em curso no órgão. */
export interface MtrPdfDownloadResult {
  url: string | null;
  mtr_pdf_sync_status?: PdfSyncStatus | null;
  mtr_pdf_sync_error_message?: string | null;
}

/** Resposta de `POST /v1/cdfs/{number}/download`. Mesma semântica da do MTR. */
export interface CdfPdfDownloadResult {
  url: string | null;
  cdf_pdf_sync_status?: PdfSyncStatus | null;
  cdf_pdf_sync_error_message?: string | null;
}

/** Mesmo shape que o gateway devolvia; os modais de MTR/CDF leem `mtr`/`cdf`. */
export interface CollectMtrFile {
  mtr: string | null;
}

export interface CollectCdfFile {
  cdf: string | null;
}

interface Page<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export class MtrManagerApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'MtrManagerApiError';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** DRF: `{ detail }` para erro simples, `{ campo: [mensagens] }` para validação. */
type ApiErrorBody = { detail?: string } & Record<string, unknown>;

export function messageFromBody(body: ApiErrorBody | null, status: number): string {
  if (typeof body?.detail === 'string' && body.detail) return body.detail;

  const fieldErrors = Object.values(body ?? {})
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === 'string');
  if (fieldErrors.length) return fieldErrors.join(' ');

  return `Erro ${status} no serviço de MTR`;
}

interface RequestInit {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const token = await getValidToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, Accept: 'application/json' };
  if (init?.body != null) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_URL}${path}`, {
    method: init?.method ?? 'GET',
    headers,
    body: init?.body != null ? JSON.stringify(init.body) : undefined,
    signal: init?.signal,
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new MtrManagerApiError(res.status, messageFromBody(body, res.status));
  }

  return res;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(path, init);
  return (await res.json()) as T;
}

/**
 * Baixa um arquivo (ZIP) com teto de tempo. Estourado o teto, o `fetch` rejeita com `AbortError`,
 * que os hooks traduzem em "tempo excedido".
 */
async function requestBlob(path: string, body: unknown, timeoutMs: number): Promise<Blob> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await send(path, { method: 'POST', body, signal: controller.signal });
    return await res.blob();
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchManifestsByCollect(collectId: string): Promise<Manifest[]> {
  const page = await request<Page<Manifest>>(
    `/v1/manifests?collect_id=${encodeURIComponent(collectId)}&order=-updated_at`,
  );
  return page.results ?? [];
}

export async function fetchMtrCollectsByCollect(collectId: string): Promise<MtrCollect[]> {
  const page = await request<Page<MtrCollect>>(
    `/v1/mtr-collects?collect_id=${encodeURIComponent(collectId)}`,
  );
  return page.results ?? [];
}

export async function requestManifestPdf(manifestId: number): Promise<MtrPdfDownloadResult> {
  return request(`/v1/manifests/${manifestId}/pdf/download`, { method: 'POST' });
}

export async function requestCdfPdf(cdfNumber: string, issuer: string): Promise<CdfPdfDownloadResult> {
  return request(
    `/v1/cdfs/${encodeURIComponent(cdfNumber)}/download?issuer=${encodeURIComponent(issuer)}`,
    { method: 'POST' },
  );
}

const ELIGIBLE_STATUSES = new Set(['issued', 'received']);

const hasText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim() !== '';

interface EligibilityFields {
  status: string;
  number: string | null;
  file: string | null;
  updated_at: string | null;
  deleted?: boolean;
}

/**
 * Mesma regra que o gateway aplicava em SQL: manifesto vivo, emitido ou recebido, numerado e com
 * PDF; empatando, o atualizado por último. Uma coleta pode ter mais de um manifesto (reemissão,
 * cancelamento), e é o elegível mais recente que representa o MTR dela.
 */
export function pickEligibleManifest<T extends EligibilityFields>(manifests: T[]): T | null {
  const eligible = manifests.filter(
    (m) => !m.deleted && ELIGIBLE_STATUSES.has(m.status) && hasText(m.number) && hasText(m.file),
  );
  eligible.sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  return eligible[0] ?? null;
}

/**
 * URL do PDF do MTR da coleta. `mtr` nulo quando não há manifesto elegível ou quando o PDF
 * ainda não está no S3 — nesse caso o serviço já disparou o download no órgão e a próxima
 * consulta encontra o arquivo.
 */
export async function fetchCollectMtrFile(collectId: string): Promise<CollectMtrFile> {
  const manifest = pickEligibleManifest(await fetchManifestsByCollect(collectId));
  if (!manifest) return { mtr: null };

  const result = await requestManifestPdf(manifest.id);
  return { mtr: result.url ?? null };
}

/**
 * URL do PDF do CDF da coleta: o manifesto elegível precisa ter CDF emitido (`cdf_number`). `cdf`
 * nulo quando não há CDF ou quando o PDF ainda não foi baixado do órgão.
 */
export async function fetchCollectCdfFile(collectId: string): Promise<CollectCdfFile> {
  const rows = await fetchMtrCollectsByCollect(collectId);
  const manifest = pickEligibleManifest(rows.filter((row) => hasText(row.cdf_number)));
  if (!manifest?.cdf_number) return { cdf: null };

  const result = await requestCdfPdf(manifest.cdf_number, manifest.issuer);
  return { cdf: result.url ?? null };
}

// ========== Situação de MTR e CDF por coleta ==========

/** Linha de `GET /v1/manifests/by-collect`: uma por `collect_id` pedido, na ordem pedida. */
export interface CollectDocumentStatus {
  collect_id: string;
  /** `false` também para coleta ausente ou fora do escopo — aqui não existe "desconhecido". */
  has_mtr: boolean;
  mtr_number: string | null;
  mtr_status: string | null;
  has_cdf: boolean;
  cdf_number: string | null;
  certificate_id: number | null;
}

/** Teto da rota por chamada; listas maiores vão em lotes. */
const BY_COLLECT_MAX_IDS = 500;

/**
 * MTR e CDF de cada coleta, pelo manifesto elegível mais recente. É com isto que o portal preenche
 * `has_mtr`/`has_cdf` da listagem e os números de MTR da fatura — o agreements e o faturamento
 * não enxergam o mtr-manager.
 */
export async function fetchManifestsByCollectIds(collectIds: string[]): Promise<CollectDocumentStatus[]> {
  const unique = [...new Set(collectIds.filter(Boolean))];
  if (!unique.length) return [];

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BY_COLLECT_MAX_IDS) {
    chunks.push(unique.slice(i, i + BY_COLLECT_MAX_IDS));
  }

  const pages = await Promise.all(
    chunks.map((chunk) => {
      const search = new URLSearchParams();
      chunk.forEach((id) => search.append('collect_id', id));
      return request<CollectDocumentStatus[]>(`/v1/manifests/by-collect?${search.toString()}`);
    }),
  );
  return pages.flat();
}

// ========== Download em massa de MTR e CDF (GR-498) ==========

/**
 * Filtro do lote, só sobre `mtr_manifest`: janela de `planned_date`, unidades e materiais, mais os
 * ids das coletas pré-recortadas no agreements (status, UF, planejadas ocultas). O mtr-manager não
 * enxerga a listagem de coletas; quem carrega o recorte da tela até ele é o front.
 */
export interface MtrBulkDownloadFilterInput {
  date_from: string;
  date_to: string;
  /** Ids de **estabelecimento** (nome herdado do mtr-manager); no realm `generator` cruzados com o escopo. */
  generator_id?: number[] | null;
  material_id?: number[] | null;
  /** Até 5000 por chamada. Ausente = toda coleta da janela/unidades/materiais com manifesto elegível. */
  collect_id?: string[] | null;
}

/** Teto de `collect_id` que a rota aceita. */
export const MTR_BULK_COLLECT_ID_LIMIT = 5000;

/** Nome de cada estabelecimento para o nome dos arquivos do ZIP, por id. */
export type EstablishmentNames = Record<string, string>;

export interface MtrBulkPreviewItem {
  collect_id: string;
  /** Id do estabelecimento; o front rotula. */
  generator_id: number | null;
  planned_date: string;
  material_id: number | null;
  mtr_number: string;
}

export interface MtrBulkDownloadSummary {
  total_mtrs: number;
  total_collects_with_mtr: number;
  batch_size: number;
  total_batches: number;
  /** 10 primeiros, por `planned_date` decrescente. */
  preview: MtrBulkPreviewItem[];
}

export interface CdfBulkPreviewItem {
  certificate_id: number;
  cdf_number: string;
  issuer: string;
  end_date: string | null;
  collects_count: number;
}

export interface CdfBulkDownloadSummary {
  /** Deduplicado por certificado — um CDF cobre várias coletas. */
  total_cdfs: number;
  total_collects_with_cdf: number;
  batch_size: number;
  total_batches: number;
  preview: CdfBulkPreviewItem[];
}

/** Teto do download de um lote (ZIP montado sob demanda a partir do S3). */
const BULK_DOWNLOAD_TIMEOUT_MS = 120000;

/** Resumo do download em massa de MTRs (contagens, lotes e prévia) — alimenta o modal. */
export async function fetchMtrBulkDownloadSummary(filter: MtrBulkDownloadFilterInput): Promise<MtrBulkDownloadSummary> {
  return request('/v1/manifests/bulk-download/summary', { method: 'POST', body: { filter } });
}

/** ZIP de um lote de MTRs (`batch` base 0). 404 quando o lote não tem linha nenhuma. */
export async function downloadMtrBulkBatch(
  filter: MtrBulkDownloadFilterInput,
  batch: number,
  establishmentNames: EstablishmentNames = {},
  timeoutMs = BULK_DOWNLOAD_TIMEOUT_MS,
): Promise<Blob> {
  return requestBlob('/v1/manifests/bulk-download', { filter, establishment_names: establishmentNames, batch }, timeoutMs);
}

/** Resumo do download em massa de CDFs — contagem deduplicada por certificado. */
export async function fetchCdfBulkDownloadSummary(filter: MtrBulkDownloadFilterInput): Promise<CdfBulkDownloadSummary> {
  return request('/v1/cdfs/bulk-download/summary', { method: 'POST', body: { filter } });
}

/** ZIP de um lote de CDFs (`batch` base 0). */
export async function downloadCdfBulkBatch(
  filter: MtrBulkDownloadFilterInput,
  batch: number,
  timeoutMs = BULK_DOWNLOAD_TIMEOUT_MS,
): Promise<Blob> {
  return requestBlob('/v1/cdfs/bulk-download', { filter, batch }, timeoutMs);
}

// ========== Credencial do órgão (GR-564) ==========

export interface SinirCredentialStatus {
  has_active_credential: boolean;
}

/** Há credencial ativa do órgão (default SINIR) para os geradores do token? Não expõe a credencial. */
export async function fetchSinirCredentialStatus(issuer = 'sinir'): Promise<SinirCredentialStatus> {
  return request(`/v1/generator-mtrs/status?issuer=${encodeURIComponent(issuer)}`);
}
